'use client';

import { Box, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { borderRadius, breakpoints, color, spacing } from '@/theme/tokens';
import { useTranslations } from 'next-intl';
import AlertBanner from '@/components/common/AlertBanner';
import { useCustomerDataContext } from '@/components/contexts/CustomerDataContext';
import ContractAgreement from '@/components/common/ContractAgreement';
import { useEffect, useState } from 'react';
import ContractAgreementAccepted from '@/components/common/ContractAgreementAccepted';
import { useContractsData } from '@/components/hooks/useContractsData';
import { useContractScrollGate } from '@/components/hooks/useContractScrollGate';
import LoadingSpinner from '@/components/LoadingSpinner';
import ContractAgreementPreview from '@/components/common/ContractAgreementPreview';
import { usePdfDownload } from '@/components/hooks/usePdfDownload';
import StyledLink from '@/components/common/StyledLink';

export const ContractWrapper = styled(Box)`
    padding: ${spacing[32]};
    background-color: ${color.white[0]};
    border-radius: ${borderRadius[8]};
    margin-bottom: ${spacing[16]};
    height: 800px;
    overflow-y: auto;
    overflow-x: auto;

    @media (max-width: ${breakpoints.md}) {
        padding: ${spacing[24]};
    }

    @media (max-width: ${breakpoints.sm}) {
        padding: ${spacing[16]};
        height: 600px;
    }
`;

export const SpinnerWrapper = styled(Box)`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const DownloadWrapper = styled(Box)`
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    align-content: flex-end;
    gap: ${spacing[32]};
    margin-top: ${spacing[32]};

    @media (max-width: ${breakpoints.md}) {
        margin-top: ${spacing[24]};
        flex-direction: column;
        align-items: flex-start;
        align-content: flex-start;
        gap: ${spacing[8]};
    }

    @media (max-width: ${breakpoints.sm}) {
        justify-content: center;
        align-items: center;
        text-align: center;
        margin-top: ${spacing[16]};
    }
`;

export const Accepted = styled(Box)`
    display: flex;
    align-items: center;
    gap: ${spacing[4]};
    flex: 1;

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        gap: 0;
    }
`;

export const Download = styled(Box)`
    display: flex;
    align-items: center;
    gap: ${spacing[8]};

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        gap: 0;
    }
`;

const TermsAndConditions = () => {
    const t = useTranslations('TermsAndConditions');
    const tEnforcement = useTranslations('CustomerZoneEnforcements');
    const { customerData, refreshCustomerData } = useCustomerDataContext();
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [shouldAcceptContract, setShouldAcceptContract] = useState(false);
    const { data, isLoading, errorStatus, fetchData } = useContractsData('terms-and-conditions');
    const { containerRef, hasScrolledToBottom, handleScroll } = useContractScrollGate(data?.documentHtml);
    const { downloadPdf, isGenerating } = usePdfDownload();

    const handleAcceptComplete = () => {
        setShowSuccessMessage(true);
        setShouldAcceptContract(false);
        refreshCustomerData();
        fetchData();
    };

    const handleDownloadPdf = () => {
        if (data?.documentHtml) {
            downloadPdf(data.documentHtml, 'W8ShippingTermsAndConditions');
        }
    };

    // Depend on the whole object so every refetch re-syncs the form: a cross-tab accept
    // signal hides it optimistically, and it must re-appear if the server still says not accepted.
    useEffect(() => {
        setShouldAcceptContract(customerData && customerData.areTermsAndConditionsAccepted !== true);
    }, [customerData]);

    useEffect(() => {
        if (showSuccessMessage) {
            const timer = setTimeout(() => {
                setShowSuccessMessage(false);
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [showSuccessMessage]);

    return (
        <div>
            {shouldAcceptContract && (
                <AlertBanner
                    type="warning"
                    title={tEnforcement('termsConditions.title')}
                    text={tEnforcement('termsConditions.description')}
                    style={{ marginBottom: spacing[16] }}
                />
            )}
            <ContractWrapper ref={containerRef} onScroll={handleScroll}>
                <Typography
                    variant="heading2bold"
                    component="h2"
                    color={color.gray[80]}
                    style={{ marginBottom: spacing[32] }}
                >
                    {t('title')}
                </Typography>
                {isLoading && (
                    <SpinnerWrapper>
                        <LoadingSpinner />
                    </SpinnerWrapper>
                )}
                {data?.documentHtml && (
                    // Server-generated contract HTML from the Data API; rendering it is the feature,
                    // and it sits behind the same trust boundary as the rest of the BFF.
                    // react-doctor-disable-next-line react-doctor/dangerous-html-sink
                    <ContractAgreementPreview dangerouslySetInnerHTML={{ __html: data.documentHtml }} />
                )}
            </ContractWrapper>

            <DownloadWrapper>
                {data?.acceptedAt && (
                    <Accepted>
                        <Typography variant="label5semibold" style={{ color: color.brand[50] }}>
                            {t('agreementAccept.accepted')}
                        </Typography>
                    </Accepted>
                )}

                <Download>
                    <Typography variant="label5medium">{t('agreementAccept.download')}</Typography>
                    <StyledLink
                        onClick={handleDownloadPdf}
                        tabIndex={0}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleDownloadPdf();
                            }
                        }}
                        variant="label5medium"
                        className="brand"
                        showUnderline={true}
                    >
                        {t('agreementAccept.downloadButtonText')}
                    </StyledLink>
                </Download>
            </DownloadWrapper>

            {shouldAcceptContract && (
                <ContractAgreement
                    text={t('agreementAccept.text')}
                    checkboxTooltip={t('agreementAccept.checkboxTooltip')}
                    buttonTooltip={t('agreementAccept.buttonTooltip')}
                    buttonText={t('agreementAccept.buttonText')}
                    contractType="terms-and-conditions"
                    onAcceptComplete={() => handleAcceptComplete()}
                    isScrolledToBottom={hasScrolledToBottom}
                />
            )}

            {showSuccessMessage && (
                <ContractAgreementAccepted successMessageText={t('agreementAccept.successMessageText')} />
            )}
        </div>
    );
};

export default TermsAndConditions;
